import { useEffect, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { GATILHOS_OFERECIDOS, GATILHO_POR_TIPO, type TipoDeGatilho } from '@shared/flow-triggers';

/**
 * Escolher o que coloca um contato no fluxo.
 *
 * Grava direto, sem passar pelo Salvar do canvas: o gatilho não é desenho, é
 * configuração do fluxo, e um "Alterar" que exigisse salvar o canvas depois
 * deixaria a pessoa achar que já tinha trocado.
 */
export function PainelDeGatilho({
  triggerType,
  triggerValue,
  cooldownDias,
  salvando,
  aoSalvar,
  aoFechar,
}: {
  triggerType: string | null | undefined;
  triggerValue: string | null | undefined;
  cooldownDias: number | null | undefined;
  salvando: boolean;
  aoSalvar: (tipo: string, valor: string, cooldown: number) => void;
  aoFechar: () => void;
}) {
  const [tipo, setTipo] = useState<string>(triggerType ?? 'keyword');
  const [valor, setValor] = useState<string>(triggerValue ?? '');
  const [cooldown, setCooldown] = useState<number>(cooldownDias ?? 0);

  // Reabrir o painel tem que mostrar o que está gravado, não o rascunho da
  // última vez que ficou aberto.
  useEffect(() => {
    setTipo(triggerType ?? 'keyword');
    setValor(triggerValue ?? '');
    setCooldown(cooldownDias ?? 0);
  }, [triggerType, triggerValue, cooldownDias]);

  const etiquetas = trpc.tags.list.useQuery(undefined, { retry: false });
  const listaDeEtiquetas = (etiquetas.data ?? []) as Array<{ id: number; name: string }>;

  const def = GATILHO_POR_TIPO.get(tipo as TipoDeGatilho);
  const pedeValor = !!def?.rotuloDoValor;
  const mudou =
    tipo !== (triggerType ?? 'keyword') ||
    valor.trim() !== (triggerValue ?? '').trim() ||
    cooldown !== (cooldownDias ?? 0);
  const podeSalvar = mudou && !salvando && (!def?.valorObrigatorio || !!valor.trim());

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Quando o fluxo começa?</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            Escolha o que coloca um contato neste fluxo.
          </p>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={aoFechar}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {GATILHOS_OFERECIDOS.map((g) => {
          const escolhido = g.tipo === tipo;
          return (
            <button
              key={g.tipo}
              type="button"
              onClick={() => {
                setTipo(g.tipo);
                // Valor de um gatilho não serve para o outro: a palavra-chave
                // "curso" não é nome de etiqueta. Voltar ao gatilho gravado
                // recupera o valor dele.
                setValor(g.tipo === (triggerType ?? 'keyword') ? (triggerValue ?? '') : '');
              }}
              className={`w-full flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors ${
                escolhido ? 'border-primary/40 bg-primary/[0.06]' : 'border-transparent hover:bg-muted/60'
              }`}
            >
              <span
                className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 shrink-0 ${
                  escolhido ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                }`}
              />
              <span className="min-w-0">
                <span className="block text-xs font-medium text-foreground">{g.rotulo}</span>
                <span className="block text-[11px] leading-snug text-muted-foreground">{g.descricao}</span>
              </span>
            </button>
          );
        })}

        {pedeValor && (
          <div className="pt-2">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              {def!.rotuloDoValor}
            </label>
            {def!.valorEhIdDeEtiqueta ? (
              // O despachante casa por `tagId`. Um campo de texto livre aqui
              // gravava o nome, o id ficava nulo e o fluxo disparava com
              // QUALQUER etiqueta aplicada ao contato.
              <Select value={valor || undefined} onValueChange={setValor}>
                <SelectTrigger className="text-sm h-9 mt-1">
                  <SelectValue placeholder={etiquetas.isLoading ? 'Carregando…' : 'Escolher etiqueta...'} />
                </SelectTrigger>
                <SelectContent>
                  {listaDeEtiquetas.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                  {!etiquetas.isLoading && listaDeEtiquetas.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      Nenhuma etiqueta cadastrada.
                    </div>
                  )}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && podeSalvar) aoSalvar(tipo, valor.trim(), cooldown); }}
                placeholder={def!.exemploDoValor ? `Ex.: ${def!.exemploDoValor}` : ''}
                className="text-sm h-9 mt-1"
                autoFocus
              />
            )}
            {def!.dicaDoValor && (
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{def!.dicaDoValor}</p>
            )}
          </div>
        )}

        {tipo === 'webhook' && (
          <p className="text-[11px] text-muted-foreground leading-snug pt-2">
            Depois de salvar, a URL para colar no outro sistema aparece numa faixa no topo do editor.
          </p>
        )}

        <div className="border-t pt-3 mt-3">
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            Não repetir para o mesmo contato
          </label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="number"
              min={0}
              max={365}
              value={cooldown}
              onChange={(e) => setCooldown(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
              className="text-sm h-9 w-24"
            />
            <span className="text-xs text-muted-foreground">
              {cooldown === 1 ? 'dia' : 'dias'}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
            {cooldown === 0
              ? 'Sem limite: quem disparar o gatilho de novo entra no fluxo de novo.'
              : `Quem já entrou nos últimos ${cooldown} ${cooldown === 1 ? 'dia' : 'dias'} não entra outra vez — e cada reentrada derrubaria a conversa em andamento.`}
          </p>
        </div>
      </div>

      <div className="shrink-0 border-t p-3">
        <Button
          className="w-full gap-1.5"
          size="sm"
          disabled={!podeSalvar}
          onClick={() => aoSalvar(tipo, valor.trim(), cooldown)}
        >
          {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          {salvando ? 'Salvando…' : mudou ? 'Salvar' : 'Salvo'}
        </Button>
      </div>
    </div>
  );
}
